import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { RealTimeProvider } from "./context/RealTimeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import CustomerSidebar from "./components/CustomerSidebar";
import CustomerTopBar from "./components/CustomerTopBar";
import UnifiedLogin from "./pages/UnifiedLogin";
import CustomerChangePassword from "./pages/CustomerChangePassword";
import CustomerRegister from "./pages/CustomerRegister";
import ResetPassword from "./pages/ResetPassword";
import ResetPasswordConfirm from "./pages/ResetPasswordConfirm";
import VerifyEmail from "./pages/VerifyEmail";
import ResendVerification from "./pages/ResendVerification";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import CustomerDashboard from "./pages/CustomerDashboard";
import CustomerProducts from "./pages/CustomerProducts";
import CustomerCart from "./pages/CustomerCart";
import CustomerManagerDashboard from "./pages/CustomerManagerDashboard";
import CustomerOrders from "./pages/CustomerOrders";
import CustomerProfile from "./pages/CustomerProfile";
import CustomerInvoices from "./pages/CustomerInvoices";
import CustomerPayables from "./pages/CustomerPayables";
import Customers from "./pages/Customers";
import Products from "./pages/Products";
import AddProduct from "./pages/AddProduct";
import EditProduct from "./pages/EditProduct";
import Categories from "./pages/Categories";
import Orders from "./pages/Orders";
import DeliveryOrders from "./pages/DeliveryOrders";
import CreateDeliveryOrder from "./pages/CreateDeliveryOrder";
import CreateInvoice from "./pages/CreateInvoice";
import ViewPurchaseOrder from "./pages/ViewPurchaseOrder";
import Invoices from "./pages/Invoices";
import Reports from "./pages/Reports";
import CRM from "./pages/CRM";
import InventoryReports from "./pages/InventoryReports";
import UserManagement from "./pages/UserManagement";
import ProductDetails from "./pages/ProductDetails";
import ManagerSetPassword from "./pages/ManagerSetPassword";
import SalesmanDashboard from "./pages/SalesmanDashboard";
import SalesmanChangePassword from "./pages/SalesmanChangePassword";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import InventoryDashboard from "./pages/InventoryDashboard";
import AddInventory from "./pages/AddInventory";
import EditInventory from "./pages/EditInventory";
import Suppliers from "./pages/Suppliers";
import CompanySetup from "./components/CompanySetup";
import CompanyAdministration from "./pages/CompanyAdministration";
import Notifications from "./pages/Notifications";
import "./App.css";
// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <h1 className="text-xl font-bold text-red-600 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-4">
              {this.state.error?.message || "An unexpected error occurred"}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <RealTimeProvider>
          <CartProvider>
            <Router>
              <div className="App">
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: "#363636",
                      color: "#fff",
                    },
                    success: {
                      duration: 3000,
                      iconTheme: {
                        primary: "#10b981",
                        secondary: "#fff",
                      },
                    },
                    error: {
                      duration: 5000,
                      iconTheme: {
                        primary: "#ef4444",
                        secondary: "#fff",
                      },
                    },
                  }}
                />
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<Landing />} />
                  <Route path="/login" element={<UnifiedLogin />} />
                  {/* Redirect old login paths to unified login */}
                  <Route
                    path="/customer/login"
                    element={<Navigate to="/login" replace />}
                  />
                  <Route
                    path="/admin/login"
                    element={<Navigate to="/login" replace />}
                  />
                  <Route
                    path="/customer/reset-password"
                    element={<ResetPassword />}
                  />
                  <Route
                    path="/reset-password-confirm"
                    element={<ResetPasswordConfirm />}
                  />
                  <Route
                    path="/verify-email/:token"
                    element={<VerifyEmail />}
                  />
                  <Route
                    path="/resend-verification"
                    element={<ResendVerification />}
                  />
                  <Route path="/register" element={<CustomerRegister />} />
                  <Route
                    path="/manager/set-password"
                    element={<ManagerSetPassword />}
                  />

                  {/* Protected admin routes */}
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected customer routes */}
                  <Route
                    path="/customer/*"
                    element={
                      <ProtectedRoute requiredRole="customer">
                        <CustomerLayout />
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected customer manager routes */}
                  <Route
                    path="/manager-dashboard"
                    element={
                      <ProtectedRoute requiredRole="manager">
                        <CustomerManagerDashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected salesman route */}
                  <Route
                    path="/salesman/dashboard"
                    element={
                      <ProtectedRoute requiredRole="sales_executive">
                        <SalesmanDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/salesman/change-password"
                    element={
                      <ProtectedRoute requiredRole="sales_executive">
                        <SalesmanChangePassword />
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected employee route */}
                  <Route
                    path="/employee/dashboard"
                    element={
                      <ProtectedRoute requiredRole="employee">
                        <EmployeeDashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* Default redirect */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
            </Router>
          </CartProvider>
        </RealTimeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

// Admin layout component
function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = React.useState(() => {
    const saved = localStorage.getItem("sidebarOpen");
    return saved !== null ? JSON.parse(saved) : true; // Default to open on desktop
  });

  React.useEffect(() => {
    localStorage.setItem("sidebarOpen", JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden transition-colors duration-200">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div
        className={`flex-1 flex flex-col min-h-screen overflow-hidden transition-all duration-300 ease-out ${
          sidebarOpen ? "lg:ml-72" : ""
        }`}
      >
        <TopBar
          isSidebarOpen={sidebarOpen}
          onSidebarOpen={() => setSidebarOpen(!sidebarOpen)}
        />

        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
          <div className="animate-fade-in">
            <Routes>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="customers" element={<Customers />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="products" element={<Products />} />
              <Route path="products/add" element={<AddProduct />} />
              <Route path="products/edit/:id" element={<EditProduct />} />
              <Route path="categories" element={<Categories />} />
              <Route path="inventory" element={<InventoryDashboard />} />
              <Route path="inventory/add" element={<AddInventory />} />
              <Route path="inventory/edit/:id" element={<EditInventory />} />
              <Route path="suppliers" element={<Suppliers />} />
              <Route path="orders" element={<Orders />} />
              <Route path="delivery-orders" element={<DeliveryOrders />} />
              <Route
                path="orders/delivery/:orderId"
                element={<CreateDeliveryOrder />}
              />
              <Route
                path="orders/invoice/:orderId"
                element={<CreateInvoice />}
              />
              <Route
                path="orders/view/:orderId"
                element={<ViewPurchaseOrder />}
              />
              <Route path="invoices" element={<Invoices />} />
              <Route path="reports" element={<Reports />} />
              <Route path="crm" element={<CRM />} />
              <Route path="inventory-reports" element={<InventoryReports />} />
              <Route path="company-setup" element={<CompanySetup />} />
              <Route
                path="company-administration"
                element={<CompanyAdministration />}
              />
              <Route path="/" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

// Customer layout component
function CustomerLayout() {
  const [sidebarOpen, setSidebarOpen] = React.useState(() => {
    const saved = localStorage.getItem("customerSidebarOpen");
    return saved !== null ? JSON.parse(saved) : true; // Default to open on desktop
  });

  React.useEffect(() => {
    localStorage.setItem("customerSidebarOpen", JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden transition-colors duration-200">
      <CustomerSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div
        className={`flex-1 flex flex-col min-h-screen overflow-hidden transition-all duration-300 ease-out ${
          sidebarOpen ? "lg:ml-72" : ""
        }`}
      >
        <CustomerTopBar
          isSidebarOpen={sidebarOpen}
          onSidebarOpen={() => setSidebarOpen(!sidebarOpen)}
        />

        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
          <div className="animate-fade-in">
            <Routes>
              <Route path="dashboard" element={<CustomerDashboard />} />
              <Route path="products" element={<CustomerProducts />} />
              <Route path="cart" element={<CustomerCart />} />
              <Route path="orders" element={<CustomerOrders />} />
              <Route path="invoices" element={<CustomerInvoices />} />
              <Route path="payables" element={<CustomerPayables />} />
              <Route path="profile" element={<CustomerProfile />} />
              <Route
                path="change-password"
                element={<CustomerChangePassword />}
              />
              <Route path="product/:productId" element={<ProductDetails />} />
              <Route path="/" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;
